import React, { useState, useEffect, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { LoaderCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { useAppStore } from "@/hooks/useAppStore";
import { createConsistentUsernameFromUUID, isAllowedUsername } from "@/lib/userUtils";
import * as playerService from "@/services/playerService";
import { useTranslation } from "@/lib/translate";

interface SettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const ErrorMap: { [key: string]: string } = {
  "ErrInvalidUsernameLength": "Username is too short, please try again.",
  "ErrUsernameAlreadyTaken": "The given username is taken, please try another one."
}

export const SettingsDialog: React.FC<SettingsDialogProps> = ({
  open,
  onOpenChange,
}) => {
  const preferredLanguage = useAppStore((state) => state.preferredLanguage);
  const t = useTranslation(preferredLanguage);
  const user = useAppStore(state => state.user);
  const updateDisplayUsername = useAppStore((state) => state.updateDisplayUsername);
  const [username, setUsername] = useState("");
  const [userId] = useState(user?.userId);
  const [userData, setUserData] = useState<any>({});
  const [isLoading, setIsLoading] = useState(true);
  const usernameFieldRef = useRef<HTMLInputElement | null>(null);
  const [fullName, setFullName] = useState(user?.firstName || "...");
  const isGuest = user && user.provider == "internal";

  const onFullNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFullName(e.target.value);
  };

  const onUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      if (user && user.userId && username) {
        if (!isAllowedUsername(username)) {
          if (!userData?.name) {
            alert(`The username ${username} is a system generated one. Please choose a different one.`);
          } else {
            alert(`The username you entered is not available or reserved. Please choose a different one.`);
          }
          usernameFieldRef?.current?.focus();
          return;
        }
        await playerService.updatePlayerData(user.userId, username, fullName);
        updateDisplayUsername(user.userId, username);
      }
      onOpenChange(false);
    } catch (e) {
      const errName = (e as any).name;
      if (errName) {
        alert((ErrorMap[errName] || (e as any).error) || `An error occurred while saving the settings, please try again!`);
      } else {
        alert(`An error occurred while saving the settings, please try again!`);
      }
      setTimeout(() => {
        usernameFieldRef.current?.focus();
      }, 100)
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (open && user?.userId) {
      setIsLoading(true);
      playerService.getUserInfo(user.userId).then((userData) => {
        console.log("[DEBUG] User:", userData);
        const data = userData.data;
        setUserData(data);
        if (data.name) {
          setFullName(data.name.trim());
          setUsername(userData.name);
        } else {
          setFullName(userData.name.trim());
          user?.userId && setUsername(createConsistentUsernameFromUUID(user.userId));
        }
      }).finally(() => {
        setIsLoading(false);
      });
    }
  }, [open, user]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-neutral-800 border-neutral-700 text-white">
        <DialogHeader>
          <DialogTitle className="text-center text-lg">
            <LoaderCircle className="inline-flex animate-spin w-4 h-4 mr-1" visibility={isLoading ? "visible" : "hidden"} /> {preferredLanguage == "ja" ? "設定" : "Profile Settings"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className={`flex justify-center py-4 ${isGuest ? "opacity-10 pointer-events-none" : ""}`}>
            <div className="grid gap-4 py-2">
              <div className="grid grid-cols-4 gap-4">
                <Label htmlFor="userId" className="text-right">
                  {preferredLanguage == "ja" ? "ユーザーID" : "ID"}
                </Label>
                <div>{userId}</div>
              </div>
              <div className="grid grid-cols-4 gap-4">
                <Label htmlFor="username" className="text-right">
                  {preferredLanguage == "ja" ? "ユーザー名。" : "Username"}
                </Label>
                <Input
                  id="username"
                  value={username}
                  disabled={isLoading}
                  onChange={onUsernameChange}
                  ref={usernameFieldRef}
                  autoFocus
                  className="col-span-3 bg-neutral-700 border-neutral-600 placeholder:text-neutral-400 focus:ring-chotto-gold"
                  placeholder={username}
                />
                <Label htmlFor="firstName" className="text-right">
                  {preferredLanguage == "ja" ? "名前。" : "Full Name"}
                </Label>
                <Input
                  id="fullName"
                  value={fullName}
                  disabled={isLoading}
                  onChange={onFullNameChange}
                  className="col-span-3 bg-neutral-700 border-neutral-600 placeholder:text-neutral-400 focus:ring-chotto-gold"
                  placeholder={isLoading ? "loading" : "Full Name"}
                />
              </div>
            </div>
          </div>
          {isGuest && <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 text-center">
            <a href="/login" className="text-blue-500 hover:text-blue-700">{t("Login")}</a> {t("to customize your username and profile.")}</div>}
          <DialogFooter className="sm:justify-center">
            <Button
              type="button"
              variant="secondary"
              onClick={() => onOpenChange(false)}>
              {t("Cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isLoading || isGuest}
              className="bg-chotto-blue hover:bg-chotto-blue/90">
              {t("Save")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
